import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import ConsultationPage from './pages/ConsultationPage';
import ConsultationInfoPage from './pages/ConsultationInfoPage';
import ConsultationHistoryPage from './pages/ConsultationHistoryPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentsInfoPage from './pages/DocumentsInfoPage';
import ResearchPage from './pages/ResearchPage';
import ResearchInfoPage from './pages/ResearchInfoPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ContractAnalysisPage from './pages/ContractAnalysisPage';
import ContractAnalysisInfoPage from './pages/ContractAnalysisInfoPage';
import SharedContractPage from './pages/SharedContractPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import ContactsPage from './pages/ContactsPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import CookiesPage from './pages/CookiesPage';
import GeneralTermsPage from './pages/GeneralTermsPage';
import StoryPage from './pages/StoryPage';
import JurisprudenceSearchPage from './pages/JurisprudenceSearchPage';
import AdminPage from './pages/AdminPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionCancelPage from './pages/SubscriptionCancelPage';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './contexts/AuthContext';
import { AccessibilityProvider } from './components/Accessibility';
import { I18nProvider } from './contexts/I18nProvider';
import { SidebarProvider } from './contexts/SidebarContext';
import { liquidGlassTheme } from './theme/liquidGlassTheme';
import { useThemeRipple } from './hooks/useTheme';
import { useAuth } from './contexts/AuthContext';
import AuthGate from './components/auth/AuthGate';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

function App() {
  // Set up theme ripple (currently light mode only)
  useThemeRipple(false);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
      <ThemeProvider theme={liquidGlassTheme}>
        <CssBaseline />
        <AccessibilityProvider>
          <AuthProvider>
            <SidebarProvider>
              <Router>
              <ScrollToTop />

              {/* Skip links removed per request */}
              
              <AppRoutes />
              </Router>
            </SidebarProvider>
          </AuthProvider>
        </AccessibilityProvider>
      </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App;

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const PUBLIC_ROUTES = ['/pricing'];
  if (!isAuthenticated && !PUBLIC_ROUTES.includes(location.pathname)) {
    return <AuthGate />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="consultation-info" element={<ConsultationInfoPage />} />
        <Route path="consultation" element={<ConsultationPage />} />
        <Route path="consultation-history" element={<ConsultationHistoryPage />} />
        <Route path="documents-info" element={<DocumentsInfoPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="contract-analysis-info" element={<ContractAnalysisInfoPage />} />
        <Route path="contract-analysis" element={<ContractAnalysisPage />} />
        <Route path="shared/:shareId" element={<SharedContractPage />} />
        <Route path="research-info" element={<ResearchInfoPage />} />
        <Route path="research" element={<ResearchPage />} />
        <Route path="pricing" element={<SubscriptionPage />} />
        <Route path="pricing/success" element={<SubscriptionSuccessPage />} />
        <Route path="pricing/canceled" element={<SubscriptionCancelPage />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="blog/:id" element={<BlogDetailPage />} />
        <Route path="pribeh" element={<StoryPage />} />
        <Route path="pribeh-pravnibota" element={<StoryPage />} />
        <Route path="kontakty" element={<ContactsPage />} />
        <Route path="podminky-pouziti" element={<TermsOfUsePage />} />
        <Route path="cookies" element={<CookiesPage />} />
        <Route path="obchodni-podminky" element={<GeneralTermsPage />} />
        <Route path="jurisprudence" element={<JurisprudenceSearchPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
